import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AuthService, UserService } from '../../Entities/AuthService';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select';
import { Alert, AlertDescription } from '@/Components/ui/alert';
import { X, Save, User, Mail, Lock, Eye, EyeOff, AlertCircle, RefreshCw, Copy, Check } from 'lucide-react';
import { validatePasswordStrength } from '../../utils/passwordValidation';
import { generateSecurePassword, copyToClipboard } from '../../utils/passwordGenerator';
import PasswordRequirements from '../PasswordRequirements';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

export default function UserForm({ user, onClose, onSuccess }) {
  useBodyScrollLock();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [passwordValidation, setPasswordValidation] = useState({
    requirements: [],
    allMet: false
  });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showGeneratedPassword, setShowGeneratedPassword] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const isEditing = !!user;
  const currentUser = AuthService.getCurrentUser();

  // Função para resetar o formulário
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'user'
    });
    setPasswordValidation({
      requirements: [],
      allMet: false
    });
    setError('');
    setValidationErrors({});
    setShowPassword(false);
    setGeneratedPassword('');
    setShowGeneratedPassword(false);
    setCopiedPassword(false);
  };

  // Gerar senha segura
  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword(12);
    setGeneratedPassword(newPassword);
    setFormData(prev => ({
      ...prev,
      password: newPassword
    }));
    const validation = validatePasswordStrength(newPassword);
    setPasswordValidation(validation);
    setShowPassword(true);
    setShowGeneratedPassword(true);
    setCopiedPassword(false);
  };

  // Copiar senha gerada
  const handleCopyPassword = async () => {
    const success = await copyToClipboard(formData.password);
    if (success) {
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'user'
      });
    } else {
      // Reset quando não há usuário (criando novo)
      resetForm();
    }
  }, [user]);

  // Reset ao desmontar o componente
  useEffect(() => {
    return () => {
      resetForm();
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validar senha em tempo real
    if (name === 'password') {
      const validation = validatePasswordStrength(value);
      setPasswordValidation(validation);
    }
    
    // Limpar erros quando usuário começar a digitar
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (error) setError('');
  };

  const handleRoleChange = (value) => {
    setFormData(prev => ({
      ...prev,
      role: value
    }));
  };

  const validateForm = () => {
    const errors = {};

    // Nome
    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
    } else if (formData.name.length < 2) {
      errors.name = 'Nome deve ter pelo menos 2 caracteres';
    }

    // Email
    if (!formData.email.trim()) {
      errors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email inválido';
    }

    // Senha (obrigatória apenas para novos usuários)
    if (!isEditing) {
      if (!formData.password) {
        errors.password = 'Senha é obrigatória';
      } else if (!passwordValidation.allMet) {
        errors.password = 'Senha não atende a todos os requisitos de segurança';
      }
    } else if (formData.password) {
      // Se está editando e forneceu nova senha, validar
      if (!passwordValidation.allMet) {
        errors.password = 'Senha não atende a todos os requisitos de segurança';
      }
    }

    // Role (apenas super admin pode criar outros admins)
    if (formData.role === 'admin' && !AuthService.isSuperAdmin()) {
      errors.role = 'Apenas super administradores podem criar administradores';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const submitData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        role: formData.role
      };

      // Adicionar senha apenas se fornecida
      if (formData.password) {
        submitData.password = formData.password;
        // Se foi gerada uma senha, enviar flag para incluir no email
        if (showGeneratedPassword) {
          submitData.sendPasswordInEmail = true;
        }
      }

      let response;
      if (isEditing) {
        // Atualizar usuário existente
        response = await UserService.update(user.id, submitData);
      } else {
        // Criar novo usuário
        response = await AuthService.register(submitData);
      }

      onSuccess(response.message || `Usuário ${isEditing ? 'atualizado' : 'criado'} com sucesso`);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const canChangeRole = () => {
    if (!isEditing) return true; // Pode definir role ao criar
    
    // Super admin pode alterar qualquer role (exceto outro super admin)
    if (AuthService.isSuperAdmin() && !user?.is_super_admin) {
      return true;
    }
    
    // ✅ CORRIGIDO: Administradores podem alterar roles de user e observer
    if (AuthService.isAdmin() && (user?.role === 'user' || user?.role === 'observer')) {
      return true;
    }
    
    return false;
  };

  // Portal: escapa do fluxo normal da página, evitando que containers com
  // margin/spacing (ex.: space-y-*) desloquem o overlay fixed.
  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 md:p-6 z-50 overflow-y-auto">
      <Card className="w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-xl 2xl:max-w-2xl my-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {isEditing ? 'Editar Usuário' : 'Novo Usuário'}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => {
            resetForm();
            onClose();
          }}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent className="max-h-[calc(100vh-8rem)] overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {/* Grid responsivo para telas grandes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              {/* Nome - ocupa 2 colunas */}
              <div className="space-y-2 lg:col-span-2">
                <label htmlFor="name" className="text-sm font-medium text-gray-700">
                  Nome Completo *
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Digite o nome completo"
                  disabled={isLoading}
                  className={validationErrors.name ? 'border-red-300' : ''}
                />
                {validationErrors.name && (
                  <p className="text-sm text-red-600">{validationErrors.name}</p>
                )}
              </div>

              {/* Email - 1 coluna */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="usuario@corumba.ms.gov.br"
                    className={`pl-10 ${validationErrors.email ? 'border-red-300' : ''}`}
                    disabled={isLoading}
                  />
                </div>
                {validationErrors.email && (
                  <p className="text-sm text-red-600">{validationErrors.email}</p>
                )}
              </div>

              {/* Role - 1 coluna */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Perfil de Acesso *
                </label>
                <Select 
                  value={formData.role} 
                  onValueChange={handleRoleChange}
                  disabled={isLoading || !canChangeRole()}
                >
                  <SelectTrigger className={validationErrors.role ? 'border-red-300' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Usuário</SelectItem>
                    <SelectItem value="observer">Observador</SelectItem>
                    {AuthService.isSuperAdmin() && (
                      <SelectItem value="admin">Administrador</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {validationErrors.role && (
                  <p className="text-sm text-red-600">{validationErrors.role}</p>
                )}
                {!canChangeRole() && (
                  <p className="text-xs text-gray-500">
                    Apenas super administradores podem alterar perfis
                  </p>
                )}
              </div>

              {/* Senha - ocupa 2 colunas */}
              <div className="space-y-2 lg:col-span-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-sm font-medium text-gray-700">
                    {isEditing ? 'Nova Senha (deixe em branco para manter)' : 'Senha *'}
                  </label>
                  {!isEditing && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleGeneratePassword}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                        disabled={isLoading}
                      >
                        <RefreshCw className="w-3 h-3" />
                        Gerar Senha
                      </button>
                      {formData.password && (
                        <button
                          type="button"
                          onClick={handleCopyPassword}
                          className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium"
                          disabled={isLoading}
                        >
                          {copiedPassword ? (
                            <><Check className="w-3 h-3" /> Copiada!</>
                          ) : (
                            <><Copy className="w-3 h-3" /> Copiar</>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={isEditing ? 'Digite nova senha (opcional)' : 'Digite a senha'}
                    className={`pl-10 pr-10 ${validationErrors.password ? 'border-red-300' : ''}`}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {validationErrors.password && (
                  <p className="text-sm text-red-600">{validationErrors.password}</p>
                )}
                
                {/* Alerta com senha gerada */}
                {showGeneratedPassword && formData.password && (
                  <Alert className="border-green-200 bg-green-50">
                    <AlertCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700 text-sm">
                      <strong>Senha gerada com sucesso!</strong> Esta senha será enviada por email para o usuário após a criação da conta.
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Requisitos da senha em tempo real */}
                {formData.password && (
                  <PasswordRequirements 
                    requirements={passwordValidation.requirements}
                    allMet={passwordValidation.allMet}
                  />
                )}
              </div>
            </div>

            {/* Botões - fora do grid */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                disabled={isLoading}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Salvando...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Save className="w-4 h-4 mr-2" />
                    {isEditing ? 'Atualizar' : 'Criar'}
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>,
    document.body
  );
}
